import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import Link from '@/routing/Link'
import { ArrowLeft, Loader2, Undo2 } from '@digital-twins/design-system/icons'
import {
  ContextMaterialIndexingStatus,
  isContextMaterialIndexingFailed,
  isContextMaterialIndexingInProgress,
  isContextMaterialIndexingReady,
  KnowledgeDocumentListItem,
  MAX_CONTEXT_MATERIAL_FILE_BYTES,
  MAX_CONTEXT_MATERIAL_FILE_COUNT,
  MAX_CONTEXT_MATERIAL_FILE_MB,
} from '@digital-twins/shared-types'
import { FileDropzone } from '@design-system/components/file-dropzone'
import { PageContent } from '@/components/layout/PageContent'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { knowledgeApi } from '@/lib/api'
import { KnowledgeIndexingBadge } from '../KnowledgeHubPage/KnowledgeIndexingBadge'
import { useKnowledgeDocumentIndexingUpdates } from '../KnowledgeHubPage/useKnowledgeDocumentIndexingUpdates'

const ACCEPTED_TYPES = '.txt,.csv,.html,.htm,.json,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.mp3,.webm,.mov,.wav,.m4a,.sav,.xlsm,.dta,.parquet,video/mp4,audio/mpeg,audio/mp3,application/x-spss-sav'

interface UploadRow {
  name: string
  status: 'pending' | 'uploaded' | 'error'
  error?: string
  document?: KnowledgeDocumentListItem
}

function progressLabel(
  rows: Array<UploadRow>,
  httpUploading: boolean,
  t: TFunction<'app'>,
): string | null {
  if (rows.length === 0) return null
  if (httpUploading) return t('library.progressQueued')
  const statuses = rows
    .map((r) => r.document?.indexingStatus)
    .filter((s): s is ContextMaterialIndexingStatus => Boolean(s))
  if (statuses.some((s) => isContextMaterialIndexingFailed(s))) {
    return t('library.progressSomeFailed')
  }
  if (statuses.some((s) => s === 'understanding')) {
    return t('library.progressPreparing')
  }
  if (statuses.some((s) => s === 'extracting' || isContextMaterialIndexingInProgress(s))) {
    return t('library.progressReading')
  }
  if (statuses.length > 0 && statuses.every((s) => isContextMaterialIndexingReady(s))) {
    return t('library.progressAllReady')
  }
  if (rows.some((r) => r.status === 'pending')) return t('library.progressQueued')
  return null
}

export default function KnowledgeUploadPage() {
  const { t } = useTranslation('app')
  const [uploading, setUploading] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const [batchId, setBatchId] = useState<string | null>(null)
  const [rows, setRows] = useState<Array<UploadRow>>([])
  const [formError, setFormError] = useState<string | null>(null)

  useKnowledgeDocumentIndexingUpdates((event) => {
    if (!event.documentToken) return
    setRows((prev) => {
      let changed = false
      const next = prev.map((row) => {
        if (!row.document || row.document.token !== event.documentToken) {
          return row
        }
        changed = true
        return {
          ...row,
          document: {
            ...row.document,
            indexingStatus: event.indexingStatus as ContextMaterialIndexingStatus,
            indexingError: event.indexingError,
          },
        }
      })
      return changed ? next : prev
    })
  })

  const statusLine = useMemo(
    () => progressLabel(rows, uploading, t),
    [rows, uploading, t],
  )

  async function handleFilesSelected(fileList: FileList) {
    const files = Array.from(fileList)
    if (files.length === 0 || uploading) return

    if (files.length > MAX_CONTEXT_MATERIAL_FILE_COUNT) {
      setFormError(t('library.errorTooMany', { count: MAX_CONTEXT_MATERIAL_FILE_COUNT }))
      return
    }

    const oversized = files.filter((f) => f.size > MAX_CONTEXT_MATERIAL_FILE_BYTES)
    if (oversized.length > 0) {
      setFormError(t('library.errorTooLarge', { mb: MAX_CONTEXT_MATERIAL_FILE_MB }))
      return
    }

    setFormError(null)
    setUploading(true)
    setBatchId(null)
    setRows(
      files.map((f) => ({
        name: f.name,
        status: 'pending' as const,
      })),
    )

    try {
      const result = await knowledgeApi.bulkUpload(files)
      setBatchId(result.batchId)
      const byName = new Map(
        result.documents.map((doc) => [doc.originalFilename || '', doc]),
      )
      setRows(
        files.map((f) => {
          const document = byName.get(f.name)
          return document
            ? { name: f.name, status: 'uploaded' as const, document }
            : { name: f.name, status: 'uploaded' as const }
        }),
      )
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('library.errorUploadFailed')
      setRows(files.map((f) => ({ name: f.name, status: 'error', error: message })))
    } finally {
      setUploading(false)
    }
  }

  async function handleUndo() {
    if (!batchId || undoing) return
    setUndoing(true)
    setFormError(null)
    try {
      await knowledgeApi.undoBulk(batchId)
      setBatchId(null)
      setRows([])
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t('library.errorUndoFailed')
      setFormError(message)
    } finally {
      setUndoing(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <PageLayout className="max-w-3xl p-8 lg:p-8">
        <PageHeader
          leading={(
            <Button asChild variant="ghost" size="sm" data-testid="knowledge-upload-back">
              <Link href="/knowledge">
                <ArrowLeft className="mr-1 h-4 w-4" />
                {t('library.title')}
              </Link>
            </Button>
          )}
          title={(
            <Heading level={1} className="font-serif text-2xl md:text-3xl font-normal">
              {t('library.uploadHeading')}
            </Heading>
          )}
          description={t('library.uploadDescription')}
          action={
            batchId ? (
              <Button
                variant="outline"
                shape="pill"
                disabled={undoing}
                onClick={() => void handleUndo()}
                data-testid="knowledge-upload-undo"
              >
                <Undo2 className="mr-2 h-4 w-4" />
                {undoing ? t('library.undoing') : t('library.undo')}
              </Button>
            ) : null
          }
        />
        <PageContent className="gap-6">
          <FileDropzone
            accept={ACCEPTED_TYPES}
            disabled={uploading}
            multiple
            label={t('library.dropLabel')}
            hint={t('library.dropHint', {
              mb: MAX_CONTEXT_MATERIAL_FILE_MB,
              count: MAX_CONTEXT_MATERIAL_FILE_COUNT,
            })}
            onFilesSelected={handleFilesSelected}
          />

          {formError ? (
            <Text size="sm" className="text-destructive" data-testid="knowledge-upload-form-error">
              {formError}
            </Text>
          ) : null}

          {statusLine ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="knowledge-upload-progress">
              {(uploading ||
                rows.some((r) =>
                  isContextMaterialIndexingInProgress(r.document?.indexingStatus),
                )) && <Loader2 className="h-4 w-4 animate-spin" />}
              <Text as="span" size="sm" color="muted">
                {statusLine}
              </Text>
            </div>
          ) : null}

          {rows.length > 0 && (
            <ul className="space-y-2" data-testid="knowledge-upload-results">
              {rows.map((row) => (
                <li
                  key={row.name}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3"
                  data-testid="knowledge-upload-row"
                >
                  <div className="min-w-0 flex-1">
                    <Text size="sm" className="truncate font-medium">
                      {row.name}
                    </Text>
                    {row.error || row.document?.indexingError ? (
                      <Text size="xs" className="text-destructive">
                        {row.error || row.document?.indexingError}
                      </Text>
                    ) : null}
                  </div>
                  {row.document ? (
                    <KnowledgeIndexingBadge status={row.document.indexingStatus} />
                  ) : row.status === 'pending' ? (
                    <KnowledgeIndexingBadge status="uploading" />
                  ) : row.status === 'error' ? (
                    <KnowledgeIndexingBadge status="failed" />
                  ) : (
                    <KnowledgeIndexingBadge status="extracting" />
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-end">
            <Button asChild variant="ghost" data-testid="knowledge-upload-done">
              <Link href="/knowledge">{t('library.done')}</Link>
            </Button>
          </div>
        </PageContent>
      </PageLayout>
    </div>
  )
}
